import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Sparkles, CheckCircle2, XCircle, FileDown, Printer, ExternalLink, History, Clock, AlertTriangle } from "lucide-react";
import { API_BASE } from "@/lib/api";

// Public certificate verification page — no authentication required.
// The QR code on the certification page targets /verify/{cert_id}.
export default function VerifyCertificate() {
    const { cert_id } = useParams();
    const [result, setResult] = useState(null);
    const [lifecycle, setLifecycle] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [rBase, rLife] = await Promise.all([
                    fetch(`${API_BASE}/edi/verify/${cert_id}`),
                    fetch(`${API_BASE}/edi/verify/${cert_id}/lifecycle`),
                ]);
                if (!rBase.ok) throw new Error(String(rBase.status));
                setResult(await rBase.json());
                if (rLife.ok) setLifecycle((await rLife.json()).lifecycle);
            } catch (e) {
                setError(String(e));
            }
        })();
    }, [cert_id]);

    const valid = !!result?.valid;
    const lifecycleStatus = lifecycle?.status;
    const statusMeta = useMemo(() => {
        switch (lifecycleStatus) {
            case "active":     return { color: "#10B981", label: "ACTIVE",     Icon: ShieldCheck };
            case "expiring":   return { color: "#F59E0B", label: "EXPIRING",   Icon: Clock };
            case "expired":    return { color: "#EF4444", label: "EXPIRED",    Icon: AlertTriangle };
            case "revoked":    return { color: "#EF4444", label: "REVOKED",    Icon: ShieldAlert };
            case "superseded": return { color: "#6366F1", label: "SUPERSEDED", Icon: History };
            default:           return { color: "#94A3B8", label: "—",          Icon: ShieldCheck };
        }
    }, [lifecycleStatus]);

    const download = async (fmt) => {
        setDownloading(fmt);
        try {
            const resp = await fetch(`${API_BASE}/edi/verify/${cert_id}/${fmt}`);
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Verification_${cert_id}.${fmt}`;
            const blob = await resp.blob();
            if (fmt === "html") {
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 15000);
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
            }
        } catch {
            /* silent — verification page must remain public and lightweight */
        } finally {
            setDownloading(null);
        }
    };

    const publicUrl = useMemo(() => `${window.location.origin}/verify/${cert_id}`, [cert_id]);
    const qrSrc = useMemo(() =>
        `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(publicUrl)}`,
        [publicUrl]);

    const StatusIcon = statusMeta.Icon;

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="cert-verify-root">
            <div className="glass rounded-2xl max-w-3xl w-full overflow-hidden">
                <div className="p-6 border-b border-slate-800/60 flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-md grid place-items-center border ${valid ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`}>
                        {valid ? <ShieldCheck className="h-5 w-5 text-emerald-400" strokeWidth={1.5} /> : <ShieldAlert className="h-5 w-5 text-red-400" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="kicker text-[var(--sgsb-gold)] text-[10px]">SGS Bateman EPCM360™</div>
                        <div className="font-display font-black text-white text-xl tracking-tight">
                            Certificate Verification
                        </div>
                    </div>
                    {lifecycle && (
                        <div className="hidden md:flex items-center gap-2 rounded-md px-3 py-1.5 border"
                             data-testid="cert-verify-lifecycle-status"
                             style={{ borderColor: `${statusMeta.color}66`, color: statusMeta.color, background: `${statusMeta.color}14` }}>
                            <StatusIcon className="h-4 w-4" strokeWidth={2} />
                            <span className="kicker text-[10px] font-bold">{statusMeta.label}</span>
                            {typeof lifecycle.days_to_expiry === "number" && (
                                <span className="text-[10px] font-body opacity-80">· {lifecycle.days_to_expiry}d</span>
                            )}
                        </div>
                    )}
                </div>

                {error && <div className="p-6 kicker text-red-400">Verification failed to load.</div>}
                {!result && !error && <div className="p-6 kicker text-slate-500">Verifying…</div>}

                {result && (
                    <div className="p-6 space-y-5">
                        <div className="flex items-center gap-2" data-testid="cert-verify-result">
                            {valid
                                ? <CheckCircle2 className="h-5 w-5 text-emerald-400" strokeWidth={2} />
                                : <XCircle       className="h-5 w-5 text-red-400" strokeWidth={2} />}
                            <div className="font-display font-black text-white text-lg tracking-tight">
                                {valid ? "Certificate is authentic" : "Certificate is NOT valid"}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                    <div className="kicker text-slate-500 text-[10px]">Certificate ID</div>
                                    <div className="mt-1 font-display font-black text-white text-sm tracking-tight">{result.cert_id}</div>
                                </div>
                                {result.title && (
                                    <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                        <div className="kicker text-slate-500 text-[10px]">Type</div>
                                        <div className="mt-1 text-slate-100 font-body">{result.title}</div>
                                    </div>
                                )}
                                {result.issued_at && (
                                    <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                        <div className="kicker text-slate-500 text-[10px]">Issued at</div>
                                        <div className="mt-1 text-slate-100 font-body">{result.issued_at.replace("T", " ").replace("Z", " UTC")}</div>
                                    </div>
                                )}
                                {typeof result.overall_readiness_pct === "number" && (
                                    <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                        <div className="kicker text-slate-500 text-[10px]">Overall readiness</div>
                                        <div className="mt-1 font-display font-black text-[var(--sgsb-gold)] text-lg tracking-tight">{result.overall_readiness_pct}%</div>
                                    </div>
                                )}
                                {result.signature && (
                                    <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3 md:col-span-2">
                                        <div className="kicker text-slate-500 text-[10px]">Digital signature (SHA-256)</div>
                                        <code className="mt-1 block text-[var(--sgsb-gold)] text-[11px] break-all">{result.signature}</code>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col items-center gap-3">
                                <div className="rounded-md bg-white p-2">
                                    <img alt="Verification QR" src={qrSrc} width={140} height={140} data-testid="cert-verify-qr" />
                                </div>
                                <div className="text-[10px] text-slate-500 kicker text-center max-w-[140px]">
                                    Scan to re-verify
                                </div>
                            </div>
                        </div>

                        {/* Enterprise Certificate Lifecycle */}
                        {lifecycle && (
                            <div className="rounded-xl border p-4"
                                 style={{ borderColor: `${statusMeta.color}55`, background: `${statusMeta.color}0D` }}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <StatusIcon className="h-4 w-4" style={{ color: statusMeta.color }} strokeWidth={1.5} />
                                    <span className="kicker text-[10px] font-bold" style={{ color: statusMeta.color }}>Lifecycle · {statusMeta.label}</span>
                                    <div className="flex-1" />
                                    {lifecycle.valid_until && (
                                        <span className="text-[11px] text-slate-400 font-body">
                                            Valid until <b className="text-slate-200">{lifecycle.valid_until.replace("T", " ").replace("Z", " UTC")}</b>
                                        </span>
                                    )}
                                </div>
                                {lifecycle.notifications?.length > 0 && (
                                    <ul className="mt-3 space-y-1">
                                        {lifecycle.notifications.map((n, i) => (
                                            <li key={i} className="text-[12px] font-body flex items-start gap-2"
                                                style={{ color: n.level === "critical" ? "#F87171" : n.level === "warning" ? "#FBBF24" : "#94A3B8" }}>
                                                <span>{n.level === "critical" ? "●" : n.level === "warning" ? "●" : "○"}</span>
                                                <span>{n.message}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {lifecycle.audit_events?.length > 0 && (
                                    <details className="mt-3 group">
                                        <summary className="cursor-pointer text-[11px] kicker text-slate-500 group-open:text-slate-300">
                                            Audit history ({lifecycle.audit_events.length})
                                        </summary>
                                        <div className="mt-2 divide-y divide-slate-800/60">
                                            {lifecycle.audit_events.map((a, i) => (
                                                <div key={i} className="py-2 text-[11px] font-body flex items-start gap-2">
                                                    <span className="kicker text-slate-500 shrink-0">{a.at?.replace("T", " ").replace("Z", "")}</span>
                                                    <span className="font-display font-bold" style={{ color: statusMeta.color }}>{a.event}</span>
                                                    <span className="text-slate-400 flex-1">{a.detail}</span>
                                                    <span className="text-slate-500 shrink-0">{a.actor}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}

                        {result.reason && (
                            <div className="rounded-md bg-red-500/10 border border-red-500/40 p-3 text-red-300 text-sm font-body">
                                <Sparkles className="inline h-3 w-3 mr-2" strokeWidth={2} />
                                {result.reason}
                            </div>
                        )}

                        {/* Executive Verification Document Framework */}
                        <div className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <FileDown className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <span className="kicker text-[var(--sgsb-gold)] text-[10px]">Executive verification document</span>
                                <span className="ml-2 text-[10px] kicker text-slate-500">
                                    QR · digital signature · audit trail · watermarked
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    ["pdf", "PDF", FileDown],
                                    ["docx", "Word", FileDown],
                                    ["pptx", "PowerPoint", FileDown],
                                    ["html", "Printable HTML", Printer],
                                ].map(([fmt, label, Icon]) => (
                                    <button key={fmt}
                                        data-testid={`cert-verify-download-${fmt}`}
                                        onClick={() => download(fmt)}
                                        disabled={downloading}
                                        className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-3 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold)]/10 disabled:opacity-40 transition-colors duration-200"
                                    >
                                        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                                        {downloading === fmt ? "Preparing…" : label}
                                    </button>
                                ))}
                                <a
                                    href={publicUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="cert-verify-open-link"
                                    className="inline-flex items-center gap-2 rounded-md border border-slate-700 text-slate-300 px-3 py-2 font-display font-bold text-xs hover:border-[color:var(--sgsb-gold)]/50 hover:text-[var(--sgsb-gold)] transition-colors duration-200"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                                    Copyable public link
                                </a>
                            </div>
                            <div className="mt-3 text-[11px] text-slate-400 font-body">
                                Every export includes SHA-256 round-trip signature validation,
                                digital signature, audit trail, and SGS Bateman enterprise branding.
                                Suitable for board distribution, executive acceptance packs and client attachments.
                            </div>
                        </div>

                        <div className="rounded-md bg-slate-950/40 border border-slate-800 p-3 text-xs text-slate-400 font-body">
                            Verified at {result.verified_at?.replace("T", " ").replace("Z", " UTC")}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
